import { logError } from '$lib/log_error';
import '$lib/prototype.extensions';
import * as msal from '@azure/msal-browser';
import { ErrorCodeCopy } from '@df/common';
import { RouteDecodeError, RouteFailureError } from '@df/fetcher';
import LogRocket from 'logrocket';
import { deployTag } from '$lib/config';

/**
 * core-js polyfills
 * Add new polyfill here if using a feature that is marked as Baseline "Newly available" or "Limited availability" on MDN
 */
// Baseline 2024
import 'core-js/actual/promise/with-resolvers';

export async function init() {
  if (!import.meta.url.includes('localhost')) {
    LogRocket.init('usxdbd/diffinder', {
      ...(deployTag !== undefined ? { release: deployTag } : {}),
      console: {
        shouldAggregateConsoleErrors: true,
      },
    });
  }
}

/** Shown as the error page's body for codes the registry gives no `action` of its own. */
const GENERIC_RECOVERY = 'Du kan prova att ladda om sidan eller gå härifrån.';

// @ts-expect-error: Complains about error being any, but it is SvelteKits fault
export async function handleError({ error }) {
  if (error instanceof RouteFailureError) {
    logError(error, `${error.code}: ${error.failureMessage}`);
    const copy = ErrorCodeCopy[error.code];
    if (copy !== undefined) {
      return {
        message: copy.message,
        body: copy.action ?? GENERIC_RECOVERY,
        handled: true,
      };
    }
  } else if (error instanceof RouteDecodeError) {
    // A tab left open across a deploy decodes the new payload with the old schema.
    logError(error, 'Response did not match the route schema');

    return {
      message: 'Diffinder har uppdaterats',
      body: 'Ladda om sidan för att fortsätta.',
      buttonSetup: { type: 'Reload' },
      handled: true,
    };
  } else if (error instanceof msal.BrowserAuthError && error.errorCode === 'popup_window_error') {
    return {
      message: 'Tillåt pop-ups för Diffinder',
      body: 'Det ser ut som att din webbläsare blockerar pop-ups. För att du ska slippa se detta felmeddelande och för att Diffinder ska fungera optimalt bör du tillåta att Diffinder öppnar pop-ups.',
      handled: true,
    };
  } else if (typeof error === 'string' && error.includes('popup_window_error')) {
    return {
      message: 'Tillåt pop-ups för Diffinder',
      body: 'Det ser ut som att din webbläsare blockerar pop-ups. För att du ska slippa se detta felmeddelande och för att Diffinder ska fungera optimalt bör du tillåta att Diffinder öppnar pop-ups.',
      handled: true,
    };
  } else if (
    error instanceof TypeError &&
    error.message.includes('Failed to fetch dynamically imported module')
  ) {
    return {
      message: 'Diffinder har uppdaterats',
      body: '',
      handled: true,
    };
  } else if (error instanceof Error && error.message.startsWith('Not found: /cases')) {
    return {
      message: 'Du verkar ha kommit fel',
      body: 'Men vi tror att vi vet vart du vill. Om du kom hit via ett bokmärke så behöver du uppdatera bokmärket med sidan som du kommer till när du klickat på "Gå till rätt sida".',
      buttonSetup: { type: 'KnownRouteError', url: `/firm` },
      handled: true,
    };
  } else if (
    error instanceof Error &&
    (error.message.startsWith('Not found: /audit/cases') ||
      error.message.startsWith('Not found: /firm/cases') ||
      error.message.startsWith('Not found: /firm/clients'))
  ) {
    return {
      message: 'Sidan du försöker nå har flyttat',
      body: 'Om du kom hit via ett bokmärke så behöver du uppdatera bokmärket med sidan som du kommer till när du klickat på "Gå till rätt sida".',
      buttonSetup: { type: 'KnownRouteError', url: `/firm` },
      handled: true,
    };
  } else if (
    error instanceof TypeError &&
    (error.message.includes('window.LogRocket is undefined') ||
      error.message.includes(`reading 'identify'`))
  ) {
    return {
      message: 'Reklamblockerare hittad',
      body: 'Du verkar ha en reklamblockerare (AdBlocker) aktiverad. För att Diffinder-teamet ska kunna felsöka eventuella problem så behöver den stängas av för diffinder.se.',
      buttonSetup: { type: 'AdBlock' },
      handled: true,
    };
  } else if (error instanceof Error && error.message.startsWith('Redirect loop')) {
    logError(error.message, 'Redirect error');

    return {
      message: 'Kan inte logga in',
      body: 'Något gick fel när du skulle loggas in automatiskt, var god logga ut och logga in igen manuellt',
      buttonSetup: { type: 'Logout' },
      handled: true,
    };
  } else if (error instanceof DOMException) {
    logError(error.message, `DOMException error in client hook (${error.name})`);
  } else if (error instanceof Error) {
    logError(error.message, 'Error in client hook (Error)');
  } else if (error instanceof TypeError) {
    logError(error.message, 'Error in client hook (TypeError)');
  } else {
    logError(error, 'Unknown error in client hook');
  }

  if (error instanceof DOMException || error instanceof Error || error instanceof TypeError) {
    console.log(error.stack);
  }

  return {
    message: 'Whoops!',
    body: `Ett oväntat fel har inträffat. ${GENERIC_RECOVERY}`,
    handled: true,
  };
}
